import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../components/Icon.tsx';
import { ReceiptPaper } from '../components/Receipt.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, StatusBadge } from '../components/ui.tsx';
import { priceNow } from '../lib/menu.ts';
import { t, tn } from '../i18n/index.ts';
import { clock, money, qty as fmtQty } from '../lib/format.ts';
import { addSale, dropSale, retrySale, snapshot, syncNow, useOffline, type OfflineLine, type OfflineSale, type Snapshot } from '../lib/offline.ts';
import { device, useSession } from '../lib/session.tsx';
import type { Receipt } from '../lib/types.ts';
import { useUi } from '../lib/ui-state.tsx';

const lineNet = (l: OfflineLine) => Math.round(l.unit_price * l.qty);

/**
 * Where sales tax is added (United States) the rate a line pays at this till: the location's
 * rate, or none for an item that is not taxed. Null where VAT is inside the prices.
 */
const addedRate = (snap: Snapshot | null, product: string): number | null => {
  const st = snap?.catalog.sales_tax;
  if (!st?.added) return null;
  const p = snap!.catalog.products.find((x) => x.id === product);
  return p?.taxable === false ? 0 : (st.rate ?? 0);
};
/** Sales tax on a price, rounded half away from zero like the server's percentOf. */
const taxOn = (net: number, rate: number) => {
  const v = (net * Math.round(rate * 1000)) / 100000;
  return Math.sign(v) * Math.round(Math.abs(v));
};
const lineTax = (snap: Snapshot | null, l: OfflineLine) => {
  const r = addedRate(snap, l.product);
  return r === null ? 0 : taxOn(lineNet(l), r);
};
/** What the guest pays for a line: its price, plus sales tax where that is added. */
const lineTotal = (snap: Snapshot | null, l: OfflineLine) => lineNet(l) + lineTax(snap, l);
const sameLine = (a: OfflineLine, b: OfflineLine) =>
  a.product === b.product && a.unit_price === b.unit_price && a.modifiers.map((m) => m.id).join() === b.modifiers.map((m) => m.id).join();

/** VAT inside a price, rounded half away from zero like the server. */
const taxIn = (gross: number, rate: number) => {
  const v = (gross * rate) / (100 + rate);
  return Math.sign(v) * Math.round(Math.abs(v));
};

/**
 * Sells at the counter while the server cannot be reached: cash and card, from the menu
 * kept on this device. Sales wait here and go to the server as soon as it answers.
 */
export function OfflineTill() {
  const { me, can } = useSession();
  const { toast } = useUi();
  const { online, syncing, queue, waiting, failed } = useOffline();
  const [snap, setSnap] = useState<Snapshot | null>(snapshot.get);
  const [category, setCategory] = useState<string | null>(null);
  const [lines, setLines] = useState<OfflineLine[]>([]);
  const [name, setName] = useState('');
  const [options, setOptions] = useState<string | null>(null);
  const [paying, setPaying] = useState<'cash' | 'card' | null>(null);
  const [printing, setPrinting] = useState<Receipt | null>(null);

  useEffect(() => {
    if (online) void snapshot.refresh(device.register).then(setSnap);
  }, [online]);

  // Print once the sheet is on the page.
  useEffect(() => {
    if (!printing) return;
    const id = requestAnimationFrame(() => {
      window.print();
      setPrinting(null);
    });
    return () => cancelAnimationFrame(id);
  }, [printing]);

  const catalog = snap?.catalog;
  const shown = useMemo(() => {
    if (!catalog) return [];
    const cat = category ?? catalog.categories[0]?.id ?? null;
    // Combo menus need the server to split their price; offline the items are sold one by one.
    // Variants are sold one by one offline, each with its full name; the main product is never sold itself.
    return catalog.products.filter((p) => p.category === cat && p.kind !== 'combo' && !p.variant_attributes?.length);
  }, [catalog, category]);
  const total = lines.reduce((s, l) => s + lineTotal(snap, l), 0);
  const salesTax = catalog?.sales_tax?.added ? lines.reduce((s, l) => s + lineTax(snap, l), 0) : null;
  const groupsOf = (product: string) =>
    (catalog?.product_modifier_groups ?? [])
      .filter((x) => x.product === product)
      .sort((a, b) => a.sequence - b.sequence)
      .map((x) => catalog!.modifier_groups.find((g) => g.id === x.group))
      .filter((g): g is NonNullable<typeof g> => !!g);

  const add = (product: string, modifiers: OfflineLine['modifiers'] = []) => {
    const p = catalog!.products.find((x) => x.id === product)!;
    const now = priceNow(catalog!, p, new Date());
    const line: OfflineLine = {
      product,
      name: p.name,
      qty: 1,
      unit_price: now.price + modifiers.reduce((s, m) => s + m.price_delta, 0),
      modifiers,
      price_rule: now.rule,
    };
    setLines((ls) => {
      const i = ls.findIndex((l) => sameLine(l, line));
      return i >= 0 ? ls.map((l, j) => (j === i ? { ...l, qty: l.qty + 1 } : l)) : [...ls, line];
    });
  };
  const tap = (product: string) => (groupsOf(product).length ? setOptions(product) : add(product));
  const setQty = (i: number, q: number) => setLines((ls) => (q <= 0 ? ls.filter((_, j) => j !== i) : ls.map((l, j) => (j === i ? { ...l, qty: q } : l))));

  const finish = (method: 'cash' | 'card', received?: number) => {
    if (!snap?.register.session || !me) return;
    const sale = addSale({
      session: snap.register.session.id,
      name: name.trim() || t('offline.counter'),
      channel: 'counter',
      lines,
      payments: [{ method, amount: total, ...(received ? { received } : {}) }],
      total,
      by: me.name,
    });
    setPaying(null);
    setLines([]);
    setName('');
    toast(method === 'cash' && received && received > total ? t('offline.saved_change', { change: money(received - total) }) : t('offline.saved'));
    setPrinting(provisionalReceipt(snap, sale, me.name, received));
  };

  const session = snap?.register.session ?? null;
  const pending = queue.filter((x) => !x.receipt_no);
  const sent = queue.filter((x) => x.receipt_no);

  return (
    <Shell>
      <div className="page">
        <div className="page-head">
          <div>
            <h1>{t('offline.title')}</h1>
            <p className="muted">{online ? t('offline.subtitle_online') : t('offline.subtitle_offline')}</p>
          </div>
          <StatusBadge
            tone={online ? 'ok' : 'warn'}
            icon={online ? 'check' : 'wifiOff'}
            label={online ? (syncing ? t('offline.sending') : t('offline.connected')) : t('offline.no_connection')}
          />
        </div>

        {!snap ? (
          <EmptyState icon="wifiOff" title={t('offline.no_copy')} body={t('offline.no_copy_body')} />
        ) : !session ? (
          <EmptyState icon="register" title={t('offline.no_shift')} body={t('offline.no_shift_body', { register: snap.register.name })} />
        ) : (
          <div className="offline-till">
            <section className="offline-menu" aria-label={t('offline.menu')}>
              <div className="category-tabs" role="tablist">
                {snap.catalog.categories.filter((c) => snap.catalog.products.some((p) => p.category === c.id && p.kind !== 'combo')).map((c) => {
                  const on = (category ?? snap.catalog.categories[0]?.id) === c.id;
                  return (
                    <button key={c.id} role="tab" aria-selected={on} className={`category-tab tone-${c.tone}${on ? ' active' : ''}`} onClick={() => setCategory(c.id)}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
              <div className="product-grid">
                {shown.map((p) => {
                  const tone = snap.catalog.categories.find((c) => c.id === p.category)?.tone ?? 'grey';
                  return (
                    <button key={p.id} className={`product-tile tone-${tone}`} onClick={() => tap(p.id)}>
                      <span className="product-name">{p.name}</span>
                      <span className="product-foot">
                        <span className="product-price">{money(priceNow(snap.catalog, p, new Date()).price)}</span>
                        {groupsOf(p.id).length ? <span className="product-hint">{t('product.options')}</span> : null}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="muted offline-copy">{t('offline.copy_from', { at: clock(snap.saved_at), register: snap.register.name })}</p>
            </section>

            <section className="panel offline-ticket" aria-label={t('offline.ticket')}>
              <Field label={t('offline.name')}>
                <input className="input" value={name} maxLength={80} placeholder={t('offline.counter')} onChange={(e) => setName(e.target.value)} />
              </Field>
              {lines.length === 0 ? (
                <p className="muted offline-empty">{t('offline.empty')}</p>
              ) : (
                <ul className="offline-lines">
                  {lines.map((l, i) => (
                    <li key={`${l.product}-${i}`}>
                      <span className="offline-line-name">
                        <strong>{l.name}</strong>
                        {l.modifiers.length ? <span className="muted">{l.modifiers.map((m) => m.name).join(', ')}</span> : null}
                      </span>
                      <div className="stepper" role="group" aria-label={t('refund.qty_of', { name: l.name })}>
                        <IconButton icon="minus" label={t('common.less')} onClick={() => setQty(i, l.qty - 1)} />
                        <span className="stepper-value">{fmtQty(l.qty)}</span>
                        <IconButton icon="plus" label={t('common.more')} onClick={() => setQty(i, l.qty + 1)} />
                      </div>
                      <span className="tabular offline-line-total">{money(lineNet(l))}</span>
                    </li>
                  ))}
                </ul>
              )}
              {salesTax !== null && lines.length ? (
                <dl className="offline-tax">
                  <dt>{t('totals.subtotal')}</dt>
                  <dd className="tabular">{money(total - salesTax)}</dd>
                  <dt>{t('totals.sales_tax')}</dt>
                  <dd className="tabular">{money(salesTax)}</dd>
                </dl>
              ) : null}
              <div className="offline-total">
                <span>{t('totals.total')}</span>
                <strong className="tabular">{money(total)}</strong>
              </div>
              <div className="offline-pay">
                <Button variant="success" size="lg" icon="cash" disabled={!lines.length} onClick={() => setPaying('cash')}>
                  {t('pay.method_cash')}
                </Button>
                <Button variant="primary" size="lg" icon="card" disabled={!lines.length} onClick={() => setPaying('card')}>
                  {t('pay.method_card')}
                </Button>
              </div>
            </section>
          </div>
        )}

        {queue.length ? (
          <section className="panel offline-queue" aria-labelledby="offline-queue">
            <div className="panel-head">
              <h2 id="offline-queue">{t('offline.queue')}</h2>
              <span className="muted">
                {waiting ? tn('offline.waiting', waiting) : t('offline.all_sent')}
                {failed ? ` · ${tn('offline.failed', failed)}` : ''}
              </span>
              {online && waiting ? (
                <Button size="sm" icon="send" busy={syncing} onClick={() => void syncNow()}>
                  {t('offline.send_now')}
                </Button>
              ) : null}
            </div>
            <ul className="list-card" role="list">
              {[...pending, ...sent].map((s) => (
                <QueueRow key={s.client_id} s={s} canDrop={can('sales.refund')} />
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      {options && catalog ? (
        <OptionsDialog
          snap={snap!}
          product={options}
          onClose={() => setOptions(null)}
          onAdd={(mods) => {
            add(options, mods);
            setOptions(null);
          }}
        />
      ) : null}
      {paying ? <PayDialog method={paying} total={total} onClose={() => setPaying(null)} onPaid={(received) => finish(paying, received)} /> : null}
      {printing ? createPortal(<div className="print-sheet"><ReceiptPaper r={printing} /></div>, document.body) : null}
    </Shell>
  );
}

function QueueRow({ s, canDrop }: { s: OfflineSale; canDrop: boolean }) {
  const [confirm, setConfirm] = useState(false);
  return (
    <li className="list-row">
      <span className="list-icon" aria-hidden="true">
        <Icon name={s.payments[0]?.method === 'card' ? 'card' : 'cash'} size={18} />
      </span>
      <span className="list-main">
        <span className="list-title">{s.receipt_no ? t('offline.row_sent', { n: s.receipt_no, name: s.name }) : s.name}</span>
        <span className="list-sub">
          {clock(s.paid_at)} · {s.by} · {tn('offline.items', s.lines.reduce((n, l) => n + l.qty, 0))}
          {s.error ? <span className="offline-error"> · {s.error}</span> : null}
        </span>
      </span>
      <span className="list-price">{money(s.total)}</span>
      {s.receipt_no ? (
        <StatusBadge tone="ok" icon="check" label={t('offline.sent')} />
      ) : s.error ? (
        <span className="offline-row-actions">
          <StatusBadge tone="danger" icon="alert" label={t('offline.needs_attention')} />
          <Button size="sm" variant="ghost" onClick={() => retrySale(s.client_id)}>
            {t('offline.retry')}
          </Button>
          {canDrop ? (
            confirm ? (
              <Button size="sm" variant="danger" onClick={() => dropSale(s.client_id)}>
                {t('offline.drop_confirm')}
              </Button>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => setConfirm(true)}>
                {t('offline.drop')}
              </Button>
            )
          ) : null}
        </span>
      ) : (
        <StatusBadge tone="warn" icon="clock" label={t('offline.waiting_one')} />
      )}
    </li>
  );
}

function OptionsDialog({ snap, product, onClose, onAdd }: { snap: Snapshot; product: string; onClose: () => void; onAdd: (m: OfflineLine['modifiers']) => void }) {
  const c = snap.catalog;
  const p = c.products.find((x) => x.id === product)!;
  const groups = c.product_modifier_groups
    .filter((x) => x.product === product)
    .sort((a, b) => a.sequence - b.sequence)
    .map((x) => c.modifier_groups.find((g) => g.id === x.group))
    .filter((g): g is NonNullable<typeof g> => !!g);
  const [chosen, setChosen] = useState<string[]>([]);
  const toggle = (g: (typeof groups)[number], id: string) =>
    setChosen((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      const inGroup = cur.filter((x) => c.modifiers.find((m) => m.id === x)?.group === g.id);
      // A pick-one group swaps the choice.
      if (g.max_select === 1) return [...cur.filter((x) => !inGroup.includes(x)), id];
      if (g.max_select > 0 && inGroup.length >= g.max_select) return cur;
      return [...cur, id];
    });
  const missing = groups.find((g) => chosen.filter((x) => c.modifiers.find((m) => m.id === x)?.group === g.id).length < g.min_select);
  const mods = chosen.map((id) => c.modifiers.find((m) => m.id === id)!).map((m) => ({ id: m.id, name: m.name, price_delta: m.price_delta }));
  return (
    <Dialog
      title={p.name}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="plus" disabled={!!missing} onClick={() => onAdd(mods)}>
            {t('offline.add', { price: money(priceNow(c, p, new Date()).price + mods.reduce((s, m) => s + m.price_delta, 0)) })}
          </Button>
        </>
      }
    >
      {groups.map((g) => (
        <fieldset key={g.id} className="offline-group">
          <legend className="field-label">
            {g.name}
            {g.min_select ? ` · ${t('offline.required')}` : ''}
          </legend>
          <div className="chips">
            {c.modifiers
              .filter((m) => m.group === g.id)
              .map((m) => (
                <button key={m.id} className={`chip${chosen.includes(m.id) ? ' active' : ''}`} aria-pressed={chosen.includes(m.id)} onClick={() => toggle(g, m.id)}>
                  {m.name}
                  {m.price_delta ? ` +${money(m.price_delta)}` : ''}
                </button>
              ))}
          </div>
        </fieldset>
      ))}
    </Dialog>
  );
}

function PayDialog({ method, total, onClose, onPaid }: { method: 'cash' | 'card'; total: number; onClose: () => void; onPaid: (received?: number) => void }) {
  const [received, setReceived] = useState('');
  const unit = 100;
  const got = received ? Number(received) * unit : 0;
  const quick = [...new Set([total, ...[500, 1000, 2000, 5000].map((n) => Math.ceil(total / (n * unit)) * n * unit)])].filter((v) => v >= total).slice(0, 4);
  const short = method === 'cash' && got > 0 && got < total;
  return (
    <Dialog
      title={method === 'cash' ? t('offline.pay_cash', { total: money(total) }) : t('offline.pay_card', { total: money(total) })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="success" icon="check" disabled={short} onClick={() => onPaid(method === 'cash' && got ? got : undefined)} data-autofocus={method === 'card' || undefined}>
            {method === 'cash' && got > total ? t('offline.give_change', { change: money(got - total) }) : t('offline.paid')}
          </Button>
        </>
      }
    >
      {method === 'cash' ? (
        <>
          <Field label={t('offline.received')} hint={t('offline.received_hint')} error={short ? t('offline.short') : null}>
            <input className="input tabular" inputMode="numeric" value={received} onChange={(e) => setReceived(e.target.value.replace(/\D/g, '').slice(0, 7))} />
          </Field>
          <div className="chips">
            {quick.map((v) => (
              <button key={v} className={`chip${got === v ? ' active' : ''}`} onClick={() => setReceived(String(v / unit))}>
                {money(v)}
              </button>
            ))}
          </div>
        </>
      ) : (
        <p>{t('offline.card_hint')}</p>
      )}
    </Dialog>
  );
}

/** What the guest gets now: a printed proof, clearly not the fiscal receipt, which follows once sent. */
function provisionalReceipt(snap: Snapshot, sale: OfflineSale, cashier: string, received?: number): Receipt {
  const rates = new Map(snap.catalog.products.map((p) => [p.id, p.tax_rate ?? 0]));
  const added = !!snap.catalog.sales_tax?.added;
  const lines = sale.lines.map((l) => ({
    name: l.name,
    qty: l.qty,
    unit_price: l.unit_price,
    modifiers: l.modifiers.map((m) => m.name),
    price_rule: l.price_rule ?? null,
    note: null,
    discount: 0,
    total: lineTotal(snap, l),
    tax: added ? lineTax(snap, l) : taxIn(lineNet(l), rates.get(l.product) ?? 0),
    tax_rate: added ? addedRate(snap, l.product)! : (rates.get(l.product) ?? 0),
  }));
  const byRate = new Map<number, { gross: number; tax: number }>();
  for (const l of lines) {
    const r = byRate.get(l.tax_rate) ?? { gross: 0, tax: 0 };
    r.gross += l.total;
    r.tax += l.tax;
    byRate.set(l.tax_rate, r);
  }
  const vat = [...byRate.entries()].sort((a, b) => b[0] - a[0]).map(([rate, v]) => ({ rate, gross: v.gross, tax: v.tax, net: v.gross - v.tax }));
  const pay = sale.payments[0]!;
  return {
    kind: 'receipt',
    copy: false,
    printed_at: sale.paid_at,
    company: snap.company,
    order: { id: sale.client_id, number: 0, name: sale.name, channel: sale.channel, guests: 0, opened_at: sale.paid_at, closed_at: sale.paid_at, receipt_no: null },
    served_by: cashier,
    cashier,
    lines,
    vat,
    tax_added: added,
    tax_id_label: snap.tax_id_label,
    subtotal: lines.reduce((s, l) => s + l.total - (added ? l.tax : 0), 0),
    discount: 0,
    total: sale.total,
    tax: vat.reduce((s, v) => s + v.tax, 0),
    tip: 0,
    payments: [{ method: pay.method, amount: pay.amount, received: received ?? null, change: received ? received - pay.amount : 0 }],
    change: received ? received - pay.amount : 0,
    prints: 1,
    fiscal: null,
    refund: null,
    offline: true,
  };
}
